import { debug } from 'cc';
import StreamingPlayerAdapter from '../StreamingPlayerAdapter';
import WebPlayerNamespace from './web_player.js';
import StreamingError from '../../StreamingError';

let videoPlayerIndex = 0;

export type AntPlayerConfig = {
    token: string;
    httpBaseURL: string;
    streamId: string;
};

export enum AntStreamingErrorName {
    STREAM_LOAD_FAILED = 'streaming load fail',
    STREAM_DISCONNECTED = 'streaming disconnected',
    STREAM_ENDED = 'streaming ended',
}

export default class AntStreamingPlayerAdapter extends StreamingPlayerAdapter {
    private readonly _webPlayer: WebPlayerNamespace.WebPlayer;
    private _videoPlayPromise: Promise<void> = null;

    constructor(configOrWindow: AntPlayerConfig, containerElement: HTMLElement, placeHolderElement?: HTMLElement, videoPlayerId?: string) {
        super();
        this._webPlayer = new WebPlayerNamespace.WebPlayer(
            configOrWindow,
            containerElement,
            placeHolderElement,
            videoPlayerId ?? `video-player-${videoPlayerIndex++}`,
        );
        this._webPlayer.addWebRTCDataListener((data) => debug(`Data received: ${data}`));
        let hasStartPlaying = false;
        this._webPlayer.addPlayerListener((status) => {
            debug('player event: ', status);
            if (status === 'playing') {
                if (!hasStartPlaying) {
                    hasStartPlaying = true;
                    const videoElement = this._webPlayer.videojsPlayer.tech(1).el() as HTMLVideoElement;
                    videoElement.addEventListener('resize', this.onResizeCallback);
                }
                this.emit(AntStreamingPlayerAdapter.EVENT_TYPE.Play);
            } else if (status === 'error') {
                this.emit(AntStreamingPlayerAdapter.EVENT_TYPE.Error, new StreamingError(AntStreamingErrorName.STREAM_LOAD_FAILED, status));
            } else if (status === 'webrtc-error') {
                this.emit(AntStreamingPlayerAdapter.EVENT_TYPE.Error, new StreamingError(AntStreamingErrorName.STREAM_DISCONNECTED, status));
            } else if (status === 'suspend' && hasStartPlaying) {
                this.emit(AntStreamingPlayerAdapter.EVENT_TYPE.Error, new StreamingError(AntStreamingErrorName.STREAM_DISCONNECTED, status));
            } else if (status === 'abort') {
                this.emit(AntStreamingPlayerAdapter.EVENT_TYPE.Error, new StreamingError(AntStreamingErrorName.STREAM_DISCONNECTED, status));
            } else if (status === 'emptied') {
                this.emit(AntStreamingPlayerAdapter.EVENT_TYPE.Error, new StreamingError(AntStreamingErrorName.STREAM_DISCONNECTED, status));
            } else if (status === 'ended') {
                this.emit(AntStreamingPlayerAdapter.EVENT_TYPE.Error, new StreamingError(AntStreamingErrorName.STREAM_ENDED, status));
            }
        });
    }

    public get muted(): boolean {
        return this._webPlayer.videojsPlayer.muted();
    }
    public set muted(value: boolean) {
        this._webPlayer.videojsPlayer.muted(value);
    }

    public get volume(): number {
        return this._webPlayer.videojsPlayer.volume();
    }
    public set volume(value: number) {
        this._webPlayer.videojsPlayer.volume(value);
    }

    public get controls(): boolean {
        return this._webPlayer.videojsPlayer.controls();
    }
    public set controls(value: boolean) {
        this._webPlayer.videojsPlayer.controls(value);
    }

    public get playsinline(): boolean {
        return !!this._webPlayer.videojsPlayer.playsinline();
    }
    public set playsinline(value: boolean) {
        this._webPlayer.videojsPlayer.playsinline(value);
    }

    public get loop(): boolean {
        return this._webPlayer.videojsPlayer.loop();
    }
    public set loop(value: boolean) {
        this._webPlayer.videojsPlayer.loop(value);
    }

    public get height(): number {
        return this._webPlayer.videojsPlayer.videoHeight();
    }

    public get width(): number {
        return this._webPlayer.videojsPlayer.videoWidth();
    }

    public get videoElement(): HTMLVideoElement {
        return this._webPlayer.videojsPlayer.tech(1).el() as HTMLVideoElement;
    }

    public static parseURL(url: string): AntPlayerConfig {
        const urlParams = new URL(url).searchParams;
        let httpBaseURL = urlParams.get('url');
        if (httpBaseURL && !httpBaseURL.includes('://')) {
            httpBaseURL = atob(httpBaseURL);
        }
        return {
            httpBaseURL,
            token: urlParams.get('token'),
            streamId: urlParams.get('channel'),
        };
    }

    public play(config?: AntPlayerConfig): void {
        if (config) {
            Object.assign(this._webPlayer, config);
        }
        this._webPlayer.initialize().then(() => this._webPlayer.play());
    }

    public resume() {
        this._videoPlayPromise = this._webPlayer.videojsPlayer.play();
        return this._videoPlayPromise;
    }

    public pause(): void {
        (this._videoPlayPromise ?? Promise.resolve()).then(() => {
            this._webPlayer.videojsPlayer.pause();
            this._videoPlayPromise = null;
        });
    }

    public stop(): void {
        this._webPlayer.destroy();
    }
}
